import { Link } from "react-router-dom";
import { BookOpen, CircleHelp, Mail, MessageCircle } from "lucide-react";

import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const FAQS = [
  {
    question: "How do I schedule a visit?",
    answer:
      "Open Schedule from the Care menu, pick a day, and create a visit for a client and caregiver. You need scheduling create or edit permission.",
    guideTo: "/docs/schedule-visits",
    guideLabel: "Schedule & visits guide",
  },
  {
    question: "Why can’t I see Clients or Caregivers?",
    answer:
      "Access is controlled by your role. Ask an organization admin to grant the matching directory permissions, or check Roles & Permissions if you are a platform superuser.",
    guideTo: "/docs/getting-started",
    guideLabel: "Getting started guide",
  },
  {
    question: "How do I update my profile or password?",
    answer:
      "Open Account from your user menu in the sidebar to edit your name and change your password.",
    guideTo: "/docs/account",
    guideLabel: "Account guide",
  },
  {
    question: "How do I change theme or font?",
    answer:
      "Open Appearance from your avatar menu. Preferences apply only to your account.",
    guideTo: "/docs/appearance",
    guideLabel: "Appearance guide",
  },
  {
    question: "Where do notifications appear?",
    answer:
      "Use the bell in the site header. You need notifications.view to see the menu.",
    guideTo: "/docs/notifications",
    guideLabel: "Notifications guide",
  },
  {
    question: "Where do audit logs live?",
    answer:
      "Compliance → Audit Logs lists sensitive actions for your agency. You need the audit.logs.view permission to open that page.",
    guideTo: "/docs/audit-logs",
    guideLabel: "Audit logs guide",
  },
];

export function HelpPage() {
  return (
    <PageShell>
      <PageHeader
        title="Get Help"
        description="Answers to common questions and ways to reach support."
      />
      <PageBody>
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="gap-4 py-4">
            <CardHeader className="px-4 pb-0">
              <CardTitle className="flex items-center gap-2 text-base">
                <BookOpen className="h-4 w-4" />
                Product docs
              </CardTitle>
              <CardDescription>
                Reference guides for Care, directory, organization settings,
                account preferences, and platform admin—organized like a product
                manual.
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4">
              <Button asChild variant="outline">
                <Link to="/docs">Open documentation</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="gap-4 py-4">
            <CardHeader className="px-4 pb-0">
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-4 w-4" />
                Contact support
              </CardTitle>
              <CardDescription>
                Need help with access, billing, or a bug? Email Krishnasankar
                or Athulraj and include your agency name and what you were
                trying to do.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2 px-4">
              <Button asChild>
                <a href="mailto:krishnasankarofficial@gmail.com">
                  Email Krishnasankar
                </a>
              </Button>
              <Button asChild variant="outline">
                <a href="mailto:athulrajv13@gmail.com">
                  <MessageCircle className="h-4 w-4" />
                  Email Athulraj
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card className="gap-4 py-4">
          <CardHeader className="px-4 pb-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <CircleHelp className="h-4 w-4" />
              Frequently asked questions
            </CardTitle>
            <CardDescription>
              Quick answers for day-to-day work in the agency workspace.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 px-4">
            {FAQS.map((item) => (
              <div key={item.question} className="space-y-1.5">
                <h2 className="text-sm font-medium">{item.question}</h2>
                <p className="text-sm text-muted-foreground">{item.answer}</p>
                {item.guideTo && (
                  <Button asChild variant="link" className="h-auto px-0 text-sm">
                    <Link to={item.guideTo}>{item.guideLabel}</Link>
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </PageBody>
    </PageShell>
  );
}
